import {downloadCSVFiles} from '@/helpers/shadcn/utils';
import {getSasToken} from '@/services/sasToken';
import {Constants} from '@/utils/constants/commonConstants';
import {logger} from '@/helpers/logger';

/**
 * Factory that creates a leaderboard CSV download handler.
 * Shared across game review components (FreeKick, Backgammon, etc.).
 */
export const createLeaderboardDownloadHandler = (downloadFileOnBlob) => {
  return async (leaderboardFileUrl, fileName) => {
    try {
      if (!leaderboardFileUrl || !fileName) return;

      const blobFileName = leaderboardFileUrl?.split('/').pop();
      const directory = leaderboardFileUrl?.split('/input')[1];

      const sasResponse = await getSasToken(
        blobFileName,
        Constants.Leaderboard
      );
      const response = await downloadFileOnBlob(
        sasResponse,
        directory,
        blobFileName
      );

      if (!response?.responseText) {
        throw new Error('No response received from leaderboard download');
      }

      downloadCSVFiles(response.responseText, fileName);
    } catch (error) {
      logger.error('Error downloading leaderboard CSV:', error);
    }
  };
};

/**
 * Common spacing constants for game review components
 */
export const GAME_SPACING = {
  accordionGapClass: 'space-y-5',
  threeColGridClass: 'grid-cols-3 gap-6'
};

/**
 * Canonical day-of-week order matching the dropdown in the setup page.
 */
const DAYS_ORDER = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY'
];

/**
 * Format `daysOfWeekUnavailable` for display, sorted in the canonical
 * dropdown order (Monday → Sunday) and title-cased.
 */
export const formatDaysOfWeekUnavailable = (days) => {
  if (!Array.isArray(days) || days.length === 0) return '-';
  return [...days]
    .sort(
      (a, b) =>
        DAYS_ORDER.indexOf(a.toUpperCase()) -
        DAYS_ORDER.indexOf(b.toUpperCase())
    )
    .map((d) => d.charAt(0).toUpperCase() + d.slice(1).toLowerCase())
    .join(', ');
};

/**
 * Date formatter for game review dates
 */
export const formatDateYYYYMMDD = (value, getFormattedDate) =>
  getFormattedDate(value, false, '-', 'yyyy-mm-dd');

/**
 * Build the shared availability grid items used by game review screens
 * (Free Kick, Wheel of Fortune, etc.).
 */
export const buildGameAvailabilityItems = (
  activityFormData,
  getFormattedDate
) => [
  {
    label: 'Daily Play Limit',
    value:
      activityFormData?.dailyPlayLimit != null
        ? String(activityFormData.dailyPlayLimit)
        : '-'
  },
  {
    label: 'Start Date',
    value: activityFormData?.startDate
      ? formatDateYYYYMMDD(activityFormData.startDate, getFormattedDate)
      : '-'
  },
  {
    label: 'End Date',
    value: activityFormData?.endDate
      ? formatDateYYYYMMDD(activityFormData.endDate, getFormattedDate)
      : '-'
  },
  {
    label: 'Daily Display Start Time',
    value: activityFormData?.dailyDisplayStartTime || '-'
  },
  {
    label: 'Daily Display End Time',
    value: activityFormData?.dailyDisplayEndTime || '-'
  },
  {
    label: 'Days of the Week Unavailable',
    value: formatDaysOfWeekUnavailable(activityFormData?.daysOfWeekUnavailable)
  }
];
